/**
 * Mapa path → microservicio Colmena (mismo criterio que gateway-local `routes.js`).
 * Prefijos ordenados por longitud descendente para longest-prefix match.
 */
import type { ColmenaApiService } from '../../environments/environment.model';

/** Entrada de ruteo: prefijo API completo (incluye `/api/v1` cuando aplica). */
export type ApiServiceRoute = {
  path: string;
  service: ColmenaApiService;
};

/**
 * Rutas API → servicio. Más específico primero (path más largo gana).
 * Mantener alineado con `gateway-local/src/routes.js`.
 */
const ROUTES: ApiServiceRoute[] = [
  // Seguridad
  { path: '/api/v1/auth', service: 'seguridad' },
  { path: '/api/v1/admin/seguridad', service: 'seguridad' },
  { path: '/api/v1/seguridad', service: 'seguridad' },
  // Probes Nest (sin `/api/v1`). `buildColmenaApiUrl('/health')` no antepone el prefijo.
  { path: '/health', service: 'seguridad' },
  { path: '/ready', service: 'seguridad' },

  // Catálogos
  { path: '/api/v1/periods', service: 'catalogos' },
  { path: '/api/v1/recaudadores', service: 'catalogos' },
  { path: '/api/v1/parametros-sistema', service: 'catalogos' },
  { path: '/api/v1/fuentes-datos', service: 'catalogos' },
  { path: '/api/v1/configuracion-recaudador', service: 'catalogos' },
  { path: '/api/v1/configuracion-layout-archivos', service: 'catalogos' },
  { path: '/api/v1/sucursales', service: 'catalogos' },
  { path: '/api/v1/motor-reglas/catalogo-errores', service: 'catalogos' },
  { path: '/api/v1/motor-reglas/catalogo-reglas-validacion', service: 'catalogos' },
  { path: '/api/v1/motor-reglas/reglas-recaudadores', service: 'catalogos' },
  { path: '/api/v1/motor-reglas/campos-rcot', service: 'catalogos' },

  // Ingesta
  { path: '/api/v1/upload-archivo', service: 'ingesta' },
  { path: '/api/v1/archivos', service: 'ingesta' },
  { path: '/api/v1/ingesta/archivos', service: 'ingesta' },

  // Reglas runtime
  { path: '/api/v1/motor-reglas/errores-archivos', service: 'reglas' },
  { path: '/api/v1/motor-reglas', service: 'reglas' },
  { path: '/api/v1/proceso-archivo', service: 'reglas' },

  // Confirmación de pago
  { path: '/api/v1/confirmacion-pago', service: 'confirmacionPago' },

  // Matriz
  { path: '/api/v1/matriz-recaudacion', service: 'matriz' },
  { path: '/api/v1/carga-matriz', service: 'matriz' },
  { path: '/api/v1/informe-destinatarios', service: 'matriz' },

  // Auditoría
  { path: '/api/v1/auditoria', service: 'auditoria' },

  // Consultas / dashboard
  { path: '/api/v1/dashboard', service: 'consultas' },
];

/** Rutas ordenadas por path descendente (longest-prefix match). */
export const API_SERVICE_ROUTES: readonly ApiServiceRoute[] = ROUTES.sort(
  (a, b) => b.path.length - a.path.length,
);

/**
 * Resuelve el microservicio destino para una ruta API normalizada.
 *
 * @param fullPath Ruta con prefijo (`/api/v1/...`) o probe (`/health`, `/ready`).
 * @returns Servicio Colmena o `undefined` si no hay match.
 */
export function resolveApiService(fullPath: string): ColmenaApiService | undefined {
  const normalized = fullPath.startsWith('/') ? fullPath : `/${fullPath}`;
  for (const route of API_SERVICE_ROUTES) {
    if (normalized === route.path || normalized.startsWith(`${route.path}/`)) {
      return route.service;
    }
  }
  return undefined;
}
